type FeatureInitializer = (root?: ParentNode) => void;
declare const __INCLUDE_CHECKOUT_FLOW__: boolean;

type FeatureDefinition = {
  name: string;
  selector: string;
  load: () => Promise<FeatureInitializer>;
};

function lazyFeature<TModule>(
  loader: () => Promise<TModule>,
  pick: (module: TModule) => FeatureInitializer
): () => Promise<FeatureInitializer> {
  let promise: Promise<FeatureInitializer> | null = null;

  return () => {
    if (promise === null) {
      promise = loader().then((module) => pick(module));
    }

    return promise;
  };
}

export const featureRegistry: FeatureDefinition[] = [
  {
    name: 'theme-dropdowns',
    selector: '[data-bs-toggle="dropdown"]',
    load: lazyFeature(
      () => import('../modules/dropdowns'),
      (module) => module.initThemeDropdowns
    ),
  },
  {
    name: 'site-nav-desktop',
    selector: '.site-nav.site-nav--desktop',
    load: lazyFeature(
      () => import('../modules/site-nav-desktop'),
      (module) => module.initDesktopSiteNav
    ),
  },
  {
    name: 'inline-modal-trigger',
    selector: '[data-inline-modal-trigger]',
    load: lazyFeature(
      () => import('../modules/inline-modal-trigger'),
      (module) => module.initInlineModalTriggers
    ),
  },
  {
    name: 'shop-search',
    selector: '[data-shop-search], [data-shop-search-toggle], [data-shop-search-panel]',
    load: lazyFeature(
      () => import('../modules/shop-search'),
      (module) => module.initShopSearch
    ),
  },
  {
    name: 'hero-showcase',
    selector: '[data-hero-showcase]',
    load: lazyFeature(
      () => import('../modules/hero-showcase'),
      (module) => module.initHeroShowcases
    ),
  },
  {
    name: 'hero-variants',
    selector: '[data-hero-variant-group]',
    load: lazyFeature(
      () => import('../modules/hero-variants'),
      (module) => module.initHeroVariants
    ),
  },
  {
    name: 'category-filters',
    selector: '[data-category-filter-root], .cms-product-listing-facets',
    load: lazyFeature(
      () => import('../modules/category-filters'),
      (module) => module.initCategoryFilters
    ),
  },
  {
    name: 'product-configurator',
    selector: '[data-product-configurator]',
    load: lazyFeature(
      () => import('../modules/product-configurator'),
      (module) => module.initProductConfigurator
    ),
  },
  {
    name: 'product-option-lightbox',
    selector: '[data-product-option-image-trigger]',
    load: lazyFeature(
      () => import('../modules/product-option-lightbox'),
      (module) => module.initProductOptionLightboxes
    ),
  },
  {
    name: 'product-swatch-zoom',
    selector: '.product-color-thumbs .product-color-thumb img',
    load: lazyFeature(
      () => import('../modules/product-swatch-zoom'),
      (module) => module.initProductSwatchZoom
    ),
  },
  {
    name: 'product-price-sticky',
    selector: '[data-product-price-track]',
    load: lazyFeature(
      () => import('../modules/product-price-sticky'),
      (module) => module.initProductPriceSticky
    ),
  },
  {
    name: 'quantity-stepper',
    selector: '[data-quantity-stepper]',
    load: lazyFeature(
      () => import('../modules/quantity-stepper'),
      (module) => module.initQuantitySteppers
    ),
  },
  {
    name: 'account-pin-code',
    selector: '[data-account-pin-group], .account-pin-group',
    load: lazyFeature(
      () => import('../modules/account-pin-code'),
      (module) => module.initAccountPinCodeFields
    ),
  },
  {
    name: 'cart-totals',
    selector: '[data-cart-page]',
    load: lazyFeature(
      () => import('../modules/cart-totals'),
      (module) => module.initCartTotals
    ),
  },
  {
    name: 'shipping-estimator',
    selector: '[data-shipping-estimator]',
    load: lazyFeature(
      () => import('../modules/shipping-estimator'),
      (module) => module.initShippingEstimator
    ),
  },
  ...(__INCLUDE_CHECKOUT_FLOW__
    ? [{
        name: 'checkout-form',
        selector: '[data-checkout-form]',
        load: lazyFeature(
          () => import('../modules/checkout-form'),
          (module) => module.initCheckoutForm
        ),
      }]
    : [{
        name: 'checkout-address-toggles',
        selector: '[data-billing-toggle], [data-billing-address-toggle]',
        load: lazyFeature(
          () => import('../modules/checkout-address-toggles'),
          (module) => module.initCheckoutAddressToggles
        ),
      }]),
  {
    name: 'form-validation',
    selector: '[data-validate-form]',
    load: lazyFeature(
      () => import('../modules/form-validation'),
      (module) => module.initFormValidation
    ),
  },
  {
    name: 'tom-select',
    selector: 'select.form-select:not([data-native-select]), select[data-tom-select]:not([data-native-select])',
    load: lazyFeature(
      () => import('../modules/tom-select'),
      (module) => module.initTomSelectFields
    ),
  },
];
